// hooks/api/useNotifications.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { notificationService } from '@/api/services/notificationService'
import { apiClient } from '@/api/client'
import { QUERY_KEYS, MUTATION_KEYS } from '@/config/api'
import { useAuthStore } from '@/store/authStore'
import { createApiRetryHandler } from '@/utils/apiRetry'
import {
  NotificationListParams,
  BulkMarkReadRequest,
  NotificationPreferences,
  NotificationView,
} from '@/types/api/notifications'
import { FirebaseTokenRegistrationRequest } from '@/types/api/auth'

const notificationFallbackPollingInterval = () =>
  localStorage.getItem('notification_realtime_status') === 'connected'
    ? 5 * 60 * 1000
    : 30 * 1000

/**
 * Hook to get notifications list (existing)
 */
export const useNotifications = (params: NotificationListParams = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.notifications.list(params),
    queryFn: () => notificationService.getNotifications(params),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: createApiRetryHandler(3),
  })
}

/**
 * Hook to get notifications by view (Team Feed, My Work, Archive)
 * GET /notifications?entity=dispatcher&view={view}&limit={limit}
 */
export const useNotificationsByView = (
  view: NotificationView,
  limit: number = 100,
  precinctId?: string
) => {
  return useQuery({
    queryKey: ['notifications', 'view', view, limit, precinctId],
    queryFn: () =>
      notificationService.getNotificationsByView(view, limit, precinctId, null),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: createApiRetryHandler(3),
    enabled: true,
  })
}

/**
 * Hook to get a single notification (existing)
 */
export const useNotification = (notificationId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.notifications.detail(notificationId),
    queryFn: () => notificationService.getNotification(notificationId),
    enabled: !!notificationId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: createApiRetryHandler(3),
  })
}

/**
 * Hook to get recent notifications (existing)
 */
export const useRecentNotifications = (limit: number = 10) => {
  return useQuery({
    queryKey: QUERY_KEYS.notifications.list({ recent: true, limit }),
    queryFn: () => notificationService.getRecentNotifications(limit),
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
    retry: createApiRetryHandler(3),
    refetchInterval: notificationFallbackPollingInterval,
  })
}

/**
 * Hook to get unread notifications (existing)
 */
export const useUnreadNotifications = (limit: number = 50) => {
  return useQuery({
    queryKey: QUERY_KEYS.notifications.list({ unread: true, limit }),
    queryFn: () => notificationService.getUnreadNotifications(limit),
    staleTime: 45 * 1000,
    gcTime: 2 * 60 * 1000,
    retry: createApiRetryHandler(3),
    refetchInterval: notificationFallbackPollingInterval,
  })
}

/**
 * NEW: Hook to get unread notification count with badge
 * GET /notifications/unread-count
 */
export const useUnreadCount = (
  entity: 'dispatcher' | 'responder' = 'dispatcher',
  precinctId?: string
) => {
  return useQuery({
    queryKey: ['notifications', 'unread-count', entity, precinctId],
    queryFn: () => notificationService.getUnreadCount(entity, precinctId),
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
    refetchInterval: notificationFallbackPollingInterval,
    retry: createApiRetryHandler(3),
  })
}

/**
 * Hook to get notification preferences (existing)
 */
export const useNotificationPreferences = (userId?: string) => {
  const authStore = useAuthStore()
  const currentUserId = userId || authStore.user?.sub

  return useQuery({
    queryKey: QUERY_KEYS.notifications.detail(`${currentUserId}-preferences`),
    queryFn: () =>
      notificationService.getNotificationPreferences(currentUserId!),
    enabled: !!currentUserId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: createApiRetryHandler(3),
  })
}

// ============ MUTATION HOOKS ============

/**
 * Mutation hook to mark notification as read (existing)
 */
export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [MUTATION_KEYS.MARK_NOTIFICATION_READ],
    mutationFn: ({
      notificationId,
      entity,
    }: {
      notificationId: string
      entity?: 'dispatcher' | 'responder'
    }) => notificationService.markNotificationAsRead(notificationId, entity),
    onSuccess: (updatedNotification, { notificationId }) => {
      queryClient.setQueryData(
        QUERY_KEYS.notifications.detail(notificationId),
        updatedNotification
      )
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.notifications.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.notifications.list({ unread_count: true }),
      })
    },
    onError: (error: any) => {
      console.error('Failed to mark notification as read:', error)
    },
  })
}

/**
 * Mutation hook to archive a notification
 * PUT /notifications/{notification_id}?entity=dispatcher
 */
export const useArchiveNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['archive-notification'],
    mutationFn: async ({
      notificationId,
      entity = 'dispatcher',
      version = 1,
    }: {
      notificationId: string
      entity?: string
      version?: number
    }) => {
      const response = await apiClient.put(
        `/notifications/${notificationId}?entity=${entity}`,
        {
          action: 'archive',
          version,
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'view'],
      })
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      })
      // Don't show toast here - let the component handle it
    },
    onError: (error: any) => {
      console.error('Failed to archive notification:', error)
      if (error?.response?.data?.status === 'conflict') {
        toast.error('Notification was updated elsewhere. Please refresh.')
        queryClient.invalidateQueries({
          queryKey: ['notifications', 'view'],
        })
      } else {
        toast.error('Failed to archive notification')
      }
    },
  })
}

/**
 * Mutation hook to defer a notification
 * PUT /notifications/{notification_id}?entity=dispatcher
 */
export const useDeferNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['defer-notification'],
    mutationFn: async ({
      notificationId,
      version,
      entity = 'dispatcher',
      deferredUntil,
    }: {
      notificationId: string
      version: number
      entity?: string
      deferredUntil: number
    }) => {
      const response = await apiClient.put(
        `/notifications/${notificationId}?entity=${entity}`,
        {
          action: 'defer',
          version,
          deferred_until: deferredUntil,
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      })
      toast.success('Notification deferred')
    },
    onError: (error: any) => {
      console.error('Failed to defer notification:', error)
      if (error?.response?.data?.status === 'conflict') {
        toast.error('Notification was updated elsewhere. Please refresh.')
        queryClient.invalidateQueries({
          queryKey: ['notifications'],
        })
      } else {
        toast.error('Failed to defer notification')
      }
    },
  })
}

const globalPendingRequests = new Set<string>()

/**
 * Mutation hook to mark notification as seen
 * PUT /notifications/{notification_id}?entity=dispatcher
 */
export const useMarkAsSeen = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['mark-seen'],
    mutationFn: async ({
      notificationId,
      version,
      entity = 'dispatcher',
    }: {
      notificationId: string
      version: number
      entity?: string
    }) => {
      const requestKey = `${notificationId}-${version}`

      // If already in progress, return early
      if (globalPendingRequests.has(requestKey)) {
        return { already_processing: true }
      }

      globalPendingRequests.add(requestKey)

      try {
        const response = await apiClient.put(
          `/notifications/${notificationId}?entity=${entity}`,
          {
            action: 'seen',
            version,
          }
        )
        return response.data
      } finally {
        globalPendingRequests.delete(requestKey)
      }
    },
    onSuccess: data => {
      if (data && !data.already_processing) {
        // Invalidate to update the UI
        queryClient.invalidateQueries({
          queryKey: ['notifications'],
        })
      }
    },
    onError: (error: any) => {
      console.error('Failed to mark as seen:', error)
      // Silently fail - don't show toast
    },
  })
}

/**
 * Mutation hook to mark notification as act (take action)
 * PUT /notifications/{notification_id}?entity=dispatcher
 */
export const useActNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['act-notification'],
    mutationFn: async ({
      notificationId,
      version,
      note,
      entity = 'dispatcher',
    }: {
      notificationId: string
      version: number
      note?: string
      entity?: string
    }) => {
      const response = await apiClient.put(
        `/notifications/${notificationId}?entity=${entity}`,
        {
          action: 'act',
          version,
          payload: {
            note: note || 'Handled by dispatcher',
          },
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      })
      toast.success('Action taken on notification')
    },
    onError: (error: any) => {
      console.error('Failed to act on notification:', error)
      if (error?.response?.data?.status === 'conflict') {
        toast.error('Notification was updated elsewhere. Please refresh.')
        queryClient.invalidateQueries({
          queryKey: ['notifications'],
        })
      } else {
        toast.error('Failed to take action on notification')
      }
    },
  })
}

/**
 * Mutation hook to restore a notification from archive
 * PUT /notifications/{notification_id}?entity=dispatcher
 */
export const useRestoreNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['restore-notification'],
    mutationFn: async ({
      notificationId,
      version,
      entity = 'dispatcher',
    }: {
      notificationId: string
      version: number
      entity?: string
    }) => {
      const response = await apiClient.put(
        `/notifications/${notificationId}?entity=${entity}`,
        {
          action: 'restore',
          version,
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      })
      toast.success('Notification restored')
    },
    onError: (error: any) => {
      console.error('Failed to restore notification:', error)
      if (error?.response?.data?.status === 'conflict') {
        toast.error('Notification was updated elsewhere. Please refresh.')
        queryClient.invalidateQueries({
          queryKey: ['notifications'],
        })
      } else {
        toast.error('Failed to restore notification')
      }
    },
  })
}

/**
 * Mutation hook to mark notification as read (mark_seen)
 * PUT /notifications/{notification_id}?entity=dispatcher
 */
export const useMarkNotificationAsReadWithVersion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['mark-notification-read-version'],
    mutationFn: async ({
      notificationId,
      version,
      entity = 'dispatcher',
    }: {
      notificationId: string
      version: number
      entity?: string
    }) => {
      const response = await apiClient.put(
        `/notifications/${notificationId}?entity=${entity}`,
        {
          action: 'mark_seen',
          version,
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      })
    },
    onError: (error: any) => {
      console.error('Failed to mark notification as read:', error)
      if (error?.response?.data?.status === 'conflict') {
        toast.error('Notification was updated elsewhere. Please refresh.')
        queryClient.invalidateQueries({
          queryKey: ['notifications'],
        })
      }
    },
  })
}

/**
 * Mutation hook to bulk mark as read (existing)
 */
export const useBulkMarkAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [MUTATION_KEYS.MARK_NOTIFICATION_READ, 'bulk'],
    mutationFn: (data: BulkMarkReadRequest) =>
      notificationService.bulkMarkAsRead(data),
    onSuccess: result => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.notifications.all,
      })
      if (result.success_count > 0) {
        toast.success(`${result.success_count} notifications marked as read`)
      }
    },
    onError: (error: any) => {
      console.error('Failed to mark notifications as read:', error)
      toast.error(error.message || 'Failed to mark notifications as read')
    },
  })
}

/**
 * Mutation hook to mark all as read (existing)
 */
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [MUTATION_KEYS.MARK_NOTIFICATION_READ, 'all'],
    mutationFn: (entity: 'dispatcher' | 'responder' = 'dispatcher') =>
      notificationService.markAllAsRead(entity),
    onSuccess: count => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.notifications.all,
      })
      if (count > 0) {
        toast.success(`All ${count} notifications marked as read`)
      } else {
        toast('No unread notifications')
      }
    },
    onError: (error: any) => {
      console.error('Failed to mark all notifications as read:', error)
      toast.error(error.message || 'Failed to mark all notifications as read')
    },
  })
}

/**
 * Mutation hook to register Firebase token (existing)
 */
export const useRegisterFirebaseToken = () => {
  return useMutation({
    mutationKey: [MUTATION_KEYS.REGISTER_FIREBASE_TOKEN],
    mutationFn: (data: FirebaseTokenRegistrationRequest) =>
      notificationService.registerFirebaseToken(data),
    onError: (error: any) => {
      console.error('Failed to register Firebase token:', error)
    },
  })
}

/**
 * Mutation hook to update notification preferences (existing)
 */
export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_DISPATCHER, 'notification-preferences'],
    mutationFn: ({
      userId,
      preferences,
    }: {
      userId: string
      preferences: Partial<NotificationPreferences>
    }) =>
      notificationService.updateNotificationPreferences(userId, preferences),
    onSuccess: (updatedPreferences, { userId }) => {
      queryClient.setQueryData(
        QUERY_KEYS.notifications.detail(`${userId}-preferences`),
        updatedPreferences
      )
      toast.success('Notification preferences updated')
    },
    onError: (error: any) => {
      console.error('Failed to update notification preferences:', error)
      toast.error(error.message || 'Failed to update notification preferences')
    },
  })
}
